//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import { useCallback, useEffect, useRef, useState } from 'react';

import Drawer, { DrawerProps } from 'antd/lib/drawer';
import cx from 'classnames';

import { useLayoutContext } from 'pages/LayoutContext';
import usePersistedState from 'hooks/usePersistedState';

import './DrawerPanel.less';

export interface DrawerPanelProps extends DrawerProps {
  absolutePositioning?: boolean;
  additionalHeightOffset?: number;
  footer?: React.ReactChild;
  footerClassName?: string;
  height?: number;
  noPadding?: boolean;
  resizable?: boolean;
  useLandingZone?: boolean;
  width?: 'standard' | 'large' | 'xlarge' | 'full' | number;
  widthStorageKey?: string;
  children?: React.ReactNode;
}

const MIN_DRAWER_WIDTH = 250;
const MAX_DRAWER_WIDTH_FRACTION = 0.9;
// Stable fallback key used when widthStorageKey is absent — never written to
// because setPersistedWidth is only called when widthStorageKey is truthy.
const UNSET_STORAGE_KEY = 'synri-drawer-width:unset';

const DrawerPanel = ({
  absolutePositioning,
  additionalHeightOffset = 0,
  children,
  className,
  closable = true,
  footer,
  footerClassName,
  height,
  mask = false,
  maskClosable,
  noPadding,
  onClose,
  placement = 'right',
  getContainer = false,
  resizable,
  title,
  useLandingZone = false,
  visible,
  width = 'standard',
  widthStorageKey,
  ...rest
}: DrawerPanelProps) => {
  const layoutContext = useLayoutContext();

  const heightOffset = calculateHeightOffset(additionalHeightOffset);

  const baseWidth =
    width === 'standard'
      ? 350
      : width === 'large'
      ? 550
      : width === 'xlarge'
      ? 900
      : width === 'full'
      ? window.innerWidth - layoutContext.dimensions.sider.width
      : width;

  // Both hooks are always called to comply with React hook rules.
  // When widthStorageKey is provided, persistedWidth/setPersistedWidth are used (survives reload).
  // When absent, localWidth/setLocalWidth are used (current session only).
  const [persistedWidth, setPersistedWidth] = usePersistedState<number | null>(
    widthStorageKey ?? UNSET_STORAGE_KEY,
    null
  );
  const [localWidth, setLocalWidth] = useState<number | null>(null);

  const overrideWidth = widthStorageKey ? persistedWidth : localWidth;
  const setOverrideWidth = widthStorageKey ? setPersistedWidth : setLocalWidth;

  // Ref keeps the latest setter stable so handleResizeMouseDown doesn't
  // need to be recreated when usePersistedState's setter changes identity.
  const setOverrideWidthRef = useRef(setOverrideWidth);
  setOverrideWidthRef.current = setOverrideWidth;

  const [isDragging, setIsDragging] = useState(false);

  // Clamp the stored pixel value against the current viewport — the stored width
  // may have been set on a larger screen and must not overflow on a smaller one.
  const drawerWidth =
    resizable && overrideWidth !== null
      ? Math.min(overrideWidth, window.innerWidth * MAX_DRAWER_WIDTH_FRACTION)
      : baseWidth;

  // Ref to the active drag cleanup function — used to detach document listeners
  // if the component unmounts while a drag is in progress.
  const dragCleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      dragCleanupRef.current?.();
    };
  }, []);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = overrideWidth ?? baseWidth;
      setIsDragging(true);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = startX - moveEvent.clientX;
        const maxWidth = window.innerWidth * MAX_DRAWER_WIDTH_FRACTION;
        const newWidth = Math.max(MIN_DRAWER_WIDTH, Math.min(startWidth + delta, maxWidth));
        setOverrideWidthRef.current(newWidth);
      };

      const cleanup = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        dragCleanupRef.current = null;
      };

      const handleMouseUp = () => cleanup();

      dragCleanupRef.current = cleanup;
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [baseWidth, overrideWidth]
  );

  const drawerPanelClasses = cx('synri-drawer-panel', className, {
    'synri-drawer-panel--absolute': absolutePositioning,
    'synri-drawer-panel--bottom': placement === 'bottom',
    'synri-drawer-panel--no-body-padding': noPadding,
    'synri-drawer-panel--resizable': resizable,
    'synri-drawer-panel--with-footer-or-header': (!!footer && !title) || (!footer && !!title),
    'synri-drawer-panel--with-footer-and-header': !!footer && !!title,
  });

  // When the drawer is portalled to document.body (getContainer !== false) the
  // Ant Design element is rendered outside the wrapper div, so CSS selectors
  // like `.synri-drawer-panel .ant-drawer-body` must match via the Ant Design
  // element's own class instead.
  const drawerClassName = getContainer !== false ? drawerPanelClasses : undefined;

  return (
    <div className={drawerPanelClasses}>
      {useLandingZone ? <DrawerPanelLandingZone visible={visible} /> : null}
      <Drawer
        closable={closable}
        getContainer={getContainer}
        className={drawerClassName}
        height={height || `calc(100vh - ${heightOffset}px)`}
        mask={mask}
        placement={placement}
        maskClosable={maskClosable}
        onClose={onClose}
        title={title}
        visible={visible}
        width={drawerWidth}
        {...rest}>
        {resizable && (
          <div
            data-testid="DrawerPanelResizeHandle"
            className={cx('synri-drawer-panel__resize-handle', {
              'synri-drawer-panel__resize-handle--dragging': isDragging,
            })}
            onMouseDown={handleResizeMouseDown}
          />
        )}
        {children}
        {footer && (
          <div data-testid="DrawerPanelFooter" className={cx('synri-drawer-panel__footer', footerClassName)}>
            {footer}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default DrawerPanel;

interface DrawerPanelLandingZoneProps {
  visible?: boolean;
}

const DrawerPanelLandingZone = ({ visible }: DrawerPanelLandingZoneProps) => {
  return (
    <div
      data-testid="DrawerPanelLandingZone"
      className={cx('synri-drawer-panel__landing-zone', { 'synri-drawer-panel__landing-zone--open': visible })}
    />
  );
};

function calculateHeightOffset(additionalOffset = 0) {
  const mainHeaderHeight = 52;
  const topBannerHeight = 36;

  let heightOffset = mainHeaderHeight + additionalOffset;

  const numberOfBannersOnPage = document.querySelectorAll('.top-banner').length;

  for (let i = 0; i < numberOfBannersOnPage; i++) {
    heightOffset += topBannerHeight;
  }

  return heightOffset;
}
